import { createContext, useContext } from "react";

/**
 * Absolute base paths (no trailing slash) under which each client-file area is mounted. Shared
 * navigation components build links as `${basePath}/${section}` and detect the active section via
 * useMatch on the same pattern, so the two apps' different route trees stay out of shared code
 * (internal: /clients/:id/debt-settlement/..., portal: /portal/kundenakt/...).
 */
export interface ClientFileBasePaths {
  assetInventory: string;
  claims: string;
  bankruptcyChecklist: string;
  clientNote: string;
}

export interface ClientFileCapabilities {
  /**
   * Whether the current user may manage global (client-independent) proceeding participants —
   * internal supervisors only; always false in the portal.
   */
  manageGlobalParticipants: boolean;
  /**
   * Whether the current user may toggle the read state of uploaded bank statements — staff only
   * (the backend endpoint is internal); always false in the portal.
   */
  markBankStatementsRead: boolean;
  /**
   * Whether the counselor helpers of the income tab are offered (setting the follow-up questions of an
   * income to "Nein" in one step) — staff only; always false in the portal.
   */
  counselorIncomeHelpers: boolean;
  /**
   * Whether the expenses can be shared with a partner found by case number (expenses tab) — staff only
   * (the backend endpoints are internal); always false in the portal.
   */
  counselorExpensePartner: boolean;
}

export interface ClientFileContextValue {
  /** The client (Kund:in) whose file is shown — from the route in the internal app, from the caller's own client_id claim in the portal. */
  clientId: string;
  basePaths: ClientFileBasePaths;
  capabilities: ClientFileCapabilities;
}

export const ClientFileContext = createContext<ClientFileContextValue | null>(null);

export function useClientFileContext(): ClientFileContextValue {
  const value = useContext(ClientFileContext);
  if (!value) {
    throw new Error("useClientFileContext must be used within a ClientFileProvider");
  }
  return value;
}
